import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const services = [
  {
    id: 'practice-management',
    icon: 'ri-stethoscope-line',
    color: 'from-[#1B4B7F] to-[#2563a8]',
    accent: '#1B4B7F',
    tag: 'OPERATIONS',
    title: 'Practice Management',
    tagline: 'Run a Smarter Practice, Every Single Day',
    summary: 'From front-desk workflows to provider scheduling, we optimize every operational touchpoint so your practice runs like a well-oiled machine — without you lifting a finger.',
    image: 'https://public.readdy.ai/ai/img_res/16cd4ea308e95bce2e6b01d07a59bee6.jpg',
    stats: [
      { value: '40%', label: 'Reduction in no-shows' },
      { value: '3x', label: 'Faster patient throughput' },
      { value: '98%', label: 'Scheduling accuracy' },
    ],
    features: [
      { icon: 'ri-calendar-check-line', title: 'Appointment Scheduling & Optimization', desc: 'Intelligent scheduling systems that minimize gaps, reduce no-shows with automated reminders, and maximize provider utilization across all locations.' },
      { icon: 'ri-flow-chart', title: 'Patient Flow Engineering', desc: 'End-to-end patient journey mapping — from check-in to check-out — designed to eliminate bottlenecks and reduce average visit time by up to 40%.' },
      { icon: 'ri-file-list-3-line', title: 'Front-Desk & Administrative Support', desc: 'Trained administrative staff management, SOP development, and front-office protocol design to ensure consistent, professional patient experiences.' },
      { icon: 'ri-building-2-line', title: 'Multi-Location Coordination', desc: 'Centralized management infrastructure for practices with multiple sites, ensuring uniform standards, shared resources, and consolidated reporting.' },
      { icon: 'ri-customer-service-2-line', title: 'Patient Experience Programs', desc: 'Satisfaction surveys, feedback loops, and service recovery protocols that drive 5-star reviews and long-term patient loyalty.' },
      { icon: 'ri-bar-chart-box-line', title: 'Operational KPI Dashboards', desc: 'Real-time performance dashboards tracking key metrics: wait times, visit volumes, provider productivity, and operational efficiency scores.' },
    ],
    who: ['Private Practices', 'Group Clinics', 'Specialty Centers', 'Ambulatory Care Facilities'],
  },
  {
    id: 'revenue-cycle',
    icon: 'ri-line-chart-line',
    color: 'from-[#0D9488] to-[#0b7a70]',
    accent: '#0D9488',
    tag: 'REVENUE',
    title: 'Revenue Cycle Management',
    tagline: 'Capture Every Dollar You\'ve Earned',
    summary: 'Most practices lose 15–30% of revenue to billing errors, claim denials, and poor follow-up. Our RCM experts close that gap — turning your clinical work into maximum, predictable cash flow.',
    image: 'https://readdy.ai/api/search-image?query=Healthcare%20revenue%20cycle%20management%20with%20medical%20billing%20specialist%20working%20on%20computer%20with%20financial%20charts%20revenue%20graphs%20insurance%20claim%20documents%20and%20coding%20books%20in%20professional%20medical%20billing%20office%20with%20organized%20workspace%20and%20multiple%20monitors%20showing%20billing%20software&width=900&height=600&seq=svc-rcm&orientation=landscape',
    stats: [
      { value: '35%', label: 'Average revenue increase' },
      { value: '60%', label: 'Fewer claim denials' },
      { value: '<24h', label: 'Claims submission time' },
    ],
    features: [
      { icon: 'ri-code-s-slash-line', title: 'Medical Coding (ICD-10, CPT, HCPCS)', desc: 'Certified coders ensure every procedure and diagnosis is coded with precision — maximizing reimbursement while maintaining full compliance with payer guidelines.' },
      { icon: 'ri-send-plane-line', title: 'Claims Submission & Tracking', desc: 'Electronic claims submitted within 24 hours of service, with real-time tracking and proactive follow-up on every outstanding claim.' },
      { icon: 'ri-error-warning-line', title: 'Denials Management & Appeals', desc: 'Dedicated denial specialists analyze root causes, file appeals, and implement prevention protocols — recovering revenue that would otherwise be written off.' },
      { icon: 'ri-money-dollar-circle-line', title: 'Accounts Receivable Optimization', desc: 'Aggressive AR follow-up with aging bucket management, payer-specific strategies, and monthly AR reports keeping your cash flow healthy and predictable.' },
      { icon: 'ri-shake-hands-line', title: 'Payer Contract Negotiations', desc: 'Expert negotiators review your payer mix and fee schedules, renegotiating contracts to secure higher reimbursement rates across all insurance carriers.' },
      { icon: 'ri-user-heart-line', title: 'Patient Billing & Collections', desc: 'Patient-friendly billing statements, payment plan setup, and compassionate collections that preserve patient relationships while recovering balances owed.' },
    ],
    who: ['Medical Practices', 'Specialty Clinics', 'Pharmacies', 'Diagnostic Centers'],
  },
  {
    id: 'compliance',
    icon: 'ri-shield-check-line',
    color: 'from-[#7C3AED] to-[#6D28D9]',
    accent: '#7C3AED',
    tag: 'COMPLIANCE',
    title: 'Compliance & Credentialing',
    tagline: 'Stay Protected. Stay Credentialed. Stay Open.',
    summary: 'One compliance gap can cost your practice hundreds of thousands in fines — or worse, closure. Our compliance team acts as your permanent shield against regulatory risk, 365 days a year.',
    image: 'https://readdy.ai/api/search-image?query=Healthcare%20compliance%20and%20credentialing%20professional%20reviewing%20medical%20documents%20regulatory%20paperwork%20HIPAA%20compliance%20binders%20insurance%20credentialing%20files%20and%20legal%20healthcare%20documents%20in%20organized%20professional%20office%20with%20law%20books%20and%20compliance%20certificates%20on%20wall&width=900&height=600&seq=svc-compliance&orientation=landscape',
    stats: [
      { value: '100%', label: 'HIPAA audit pass rate' },
      { value: '0', label: 'Compliance violations' },
      { value: '30 days', label: 'Avg. credentialing time' },
    ],
    features: [
      { icon: 'ri-lock-password-line', title: 'HIPAA Compliance Programs', desc: 'Comprehensive HIPAA risk assessments, policy development, staff training, and ongoing monitoring to ensure your practice meets all Privacy and Security Rule requirements.' },
      { icon: 'ri-id-card-line', title: 'Provider Credentialing & Enrollment', desc: 'Full-cycle credentialing with hospitals, payers, and networks — primary source verification, application management, and re-credentialing on schedule, every time.' },
      { icon: 'ri-file-shield-2-line', title: 'Regulatory Documentation Management', desc: 'Organized, audit-ready documentation systems for all federal and state regulatory requirements including CMS, DEA, state medical board, and accreditation bodies.' },
      { icon: 'ri-search-eye-line', title: 'Internal Compliance Audits', desc: 'Proactive internal audits of billing, documentation, and operational practices — identifying vulnerabilities before regulators do.' },
      { icon: 'ri-nurse-line', title: 'OIG Exclusion Monitoring', desc: 'Monthly screening of all providers and staff against OIG, SAM, and state exclusion lists to prevent costly violations and protect your Medicare/Medicaid participation.' },
      { icon: 'ri-book-open-line', title: 'Compliance Training Programs', desc: 'Customized staff training on HIPAA, fraud & abuse laws, coding compliance, and workplace regulations — delivered online or on-site with completion tracking.' },
    ],
    who: ['All Healthcare Entities', 'Multi-Provider Groups', 'Federally Funded Practices', 'Accredited Facilities'],
  },
  {
    id: 'hr-staffing',
    icon: 'ri-team-line',
    color: 'from-[#D97706] to-[#B45309]',
    accent: '#D97706',
    tag: 'HUMAN RESOURCES',
    title: 'HR & Staffing Solutions',
    tagline: 'Build the Team That Builds Your Practice',
    summary: 'Your staff is your practice\'s most valuable asset — and its biggest operational challenge. We handle every aspect of the employee lifecycle so you attract, retain, and develop top healthcare talent.',
    image: 'https://readdy.ai/api/search-image?query=Healthcare%20human%20resources%20team%20meeting%20with%20diverse%20medical%20professionals%20and%20HR%20manager%20discussing%20staffing%20recruitment%20onboarding%20documents%20employee%20benefits%20in%20bright%20modern%20medical%20office%20conference%20room%20with%20whiteboards%20and%20professional%20healthcare%20team%20atmosphere&width=900&height=600&seq=svc-hr&orientation=landscape',
    stats: [
      { value: '85%', label: 'Staff retention rate' },
      { value: '14 days', label: 'Avg. hire time' },
      { value: '$15K+', label: 'Saved per hire' },
    ],
    features: [
      { icon: 'ri-user-search-line', title: 'Healthcare Recruitment & Talent Acquisition', desc: 'Targeted recruitment for physicians, NPs, PAs, nurses, medical assistants, and administrative staff — using our network of 10,000+ vetted healthcare professionals.' },
      { icon: 'ri-user-add-line', title: 'Onboarding & Orientation Programs', desc: 'Structured 30/60/90-day onboarding programs that get new hires productive faster, reduce early turnover, and ensure compliance from day one.' },
      { icon: 'ri-money-cny-circle-line', title: 'Payroll Processing & Benefits Administration', desc: 'Accurate, on-time payroll processing with full benefits administration — health insurance, 401(k), PTO tracking, and ACA compliance reporting.' },
      { icon: 'ri-award-line', title: 'Performance Management Systems', desc: 'Goal-setting frameworks, performance review templates, and corrective action protocols that build accountability and drive continuous improvement.' },
      { icon: 'ri-scales-3-line', title: 'Employment Law & HR Compliance', desc: 'Expert guidance on FMLA, ADA, FLSA, and state labor laws — keeping your practice protected from costly employment disputes and litigation.' },
      { icon: 'ri-graduation-cap-line', title: 'Staff Training & Development', desc: 'Ongoing clinical and administrative training programs, CEU tracking, and leadership development pathways that grow your team from within.' },
    ],
    who: ['Private Practices', 'Group Practices', 'Pharmacies', 'Laboratories'],
  },
  {
    id: 'technology',
    icon: 'ri-computer-line',
    color: 'from-[#0891B2] to-[#0E7490]',
    accent: '#0891B2',
    tag: 'TECHNOLOGY',
    title: 'Technology & IT Integration',
    tagline: 'Modern Tech. Zero Headaches.',
    summary: 'Healthcare technology should work for you — not against you. We implement, optimize, and support the digital infrastructure that powers efficient, connected, and future-ready practices.',
    image: 'https://readdy.ai/api/search-image?query=Modern%20healthcare%20technology%20integration%20with%20EHR%20electronic%20health%20records%20system%20on%20multiple%20computer%20screens%20medical%20software%20interface%20tablets%20telehealth%20platform%20and%20IT%20infrastructure%20in%20contemporary%20medical%20office%20with%20clean%20organized%20technology%20setup%20and%20healthcare%20professionals%20using%20digital%20tools&width=900&height=600&seq=svc-tech&orientation=landscape',
    stats: [
      { value: '99.9%', label: 'System uptime SLA' },
      { value: '50%', label: 'Faster EHR adoption' },
      { value: '24/7', label: 'IT support coverage' },
    ],
    features: [
      { icon: 'ri-database-2-line', title: 'EHR/EMR Implementation & Optimization', desc: 'Vendor-neutral EHR selection, full implementation, data migration, and ongoing optimization for Epic, Athenahealth, eClinicalWorks, Kareo, and all major platforms.' },
      { icon: 'ri-video-chat-line', title: 'Telehealth Platform Setup', desc: 'HIPAA-compliant telehealth infrastructure setup, provider training, and patient onboarding — enabling you to see patients virtually from day one.' },
      { icon: 'ri-shield-keyhole-line', title: 'Healthcare Cybersecurity', desc: 'Multi-layered security protocols including endpoint protection, encrypted communications, staff phishing training, and incident response planning.' },
      { icon: 'ri-links-line', title: 'System Interoperability & Integration', desc: 'Seamless integration between EHR, billing, lab, pharmacy, and imaging systems — eliminating data silos and enabling real-time information flow.' },
      { icon: 'ri-cloud-line', title: 'Cloud Infrastructure & Data Backup', desc: 'HIPAA-compliant cloud hosting, automated daily backups, and disaster recovery planning to ensure your data is always safe and accessible.' },
      { icon: 'ri-customer-service-line', title: 'Ongoing IT Help Desk Support', desc: '24/7 dedicated IT support for all staff — from password resets to critical system issues — with guaranteed response times and escalation protocols.' },
    ],
    who: ['All Practice Types', 'Multi-Site Organizations', 'Telehealth Providers', 'Diagnostic Centers'],
  },
  {
    id: 'audit-review',
    icon: 'ri-file-search-line',
    color: 'from-[#BE185D] to-[#9D174D]',
    accent: '#BE185D',
    tag: 'AUDIT & REVIEW',
    title: 'Practice Audit & Review',
    tagline: 'Find the Leaks Before They Sink You.',
    summary: 'Most practices are losing revenue, risking compliance violations, and operating inefficiently — without even knowing it. Our Practice Audit & Review service delivers a comprehensive, expert-led diagnostic across every critical area of your operation, giving you a clear picture of where you stand and exactly what to fix.',
    image: 'https://readdy.ai/api/search-image?query=Healthcare%20practice%20audit%20professional%20consultant%20reviewing%20medical%20practice%20documents%20financial%20records%20compliance%20files%20and%20operational%20reports%20at%20organized%20desk%20with%20multiple%20binders%20charts%20and%20audit%20checklists%20in%20bright%20professional%20medical%20consulting%20office%20with%20clean%20white%20background%20and%20serious%20focused%20atmosphere&width=900&height=600&seq=svc-audit-review&orientation=landscape',
    stats: [
      { value: '92%', label: 'Of audited practices find critical gaps' },
      { value: '30 days', label: 'Full audit turnaround' },
      { value: '5 areas', label: 'Comprehensively reviewed' },
    ],
    features: [
      { icon: 'ri-money-dollar-circle-line', title: 'Billing & Coding Audit', desc: 'Line-by-line review of your claims, coding accuracy, and charge capture processes — identifying undercoding, overcoding, and missed revenue opportunities that directly impact your bottom line.' },
      { icon: 'ri-shield-check-line', title: 'Compliance Risk Assessment', desc: 'Deep-dive evaluation of your HIPAA policies, documentation practices, and regulatory adherence — flagging vulnerabilities before they become costly violations or audit triggers.' },
      { icon: 'ri-settings-3-line', title: 'Operational Efficiency Review', desc: 'Assessment of patient flow, scheduling systems, front-desk protocols, and administrative workflows — pinpointing bottlenecks and inefficiencies that slow your practice down.' },
      { icon: 'ri-line-chart-line', title: 'Revenue Cycle Performance Analysis', desc: 'End-to-end RCM evaluation covering denial rates, AR aging, payer mix, collection ratios, and clean claim rates — benchmarked against industry standards for your specialty.' },
      { icon: 'ri-team-line', title: 'Staffing & HR Structure Review', desc: 'Analysis of your team composition, role clarity, compensation benchmarks, and HR compliance — identifying gaps in coverage, redundancies, and retention risks.' },
      { icon: 'ri-file-text-line', title: 'Detailed Audit Report & Action Plan', desc: 'A comprehensive written report with findings graded by severity, prioritized recommendations, and a step-by-step action plan your team can implement immediately — or hand off to us.' },
    ],
    who: ['All Practice Types', 'New MSO Clients', 'Practices Under Review', 'Growth-Stage Clinics'],
    auditTiers: [
      {
        name: 'Mini Practice Audit',
        badge: 'QUICK WIN',
        badgeColor: '#0D9488',
        desc: 'A rapid 5-area health check designed to surface your top 3 critical issues within days. Perfect as a starting point before committing to a full engagement.',
        includes: ['Billing spot-check (25 claims)', 'Compliance gap scan', 'RCM KPI snapshot', 'Staffing ratio review', 'Written summary with top findings'],
        cta: 'Request Mini Audit',
      },
      {
        name: 'Full Practice Audit',
        badge: 'COMPREHENSIVE',
        badgeColor: '#BE185D',
        desc: 'The complete diagnostic — every system, every process, every dollar. Delivered as a detailed report with graded findings and a prioritized 90-day action plan.',
        includes: ['Full billing & coding review (100+ claims)', 'HIPAA risk assessment', 'Complete RCM analysis', 'Operational workflow mapping', 'HR & staffing structure review', 'Graded findings report', '90-day action plan', 'Executive debrief call'],
        cta: 'Schedule Full Audit',
      },
    ],
  },
  {
    id: 'financial-operations',
    icon: 'ri-calculator-line',
    color: 'from-[#059669] to-[#047857]',
    accent: '#059669',
    tag: 'FINANCE',
    title: 'Financial Operations',
    tagline: 'Financial Clarity. Strategic Growth.',
    summary: 'Beyond billing, your practice needs a strong financial foundation. We provide the accounting, reporting, and strategic financial guidance that transforms a busy practice into a profitable, scalable business.',
    image: 'https://readdy.ai/api/search-image?query=Healthcare%20financial%20operations%20with%20professional%20accountant%20and%20medical%20practice%20administrator%20reviewing%20financial%20reports%20charts%20graphs%20budget%20documents%20and%20strategic%20financial%20planning%20materials%20in%20modern%20bright%20office%20with%20financial%20dashboards%20on%20screens%20and%20organized%20professional%20workspace&width=900&height=600&seq=svc-finance&orientation=landscape',
    stats: [
      { value: '25%', label: 'Average cost reduction' },
      { value: '100%', label: 'Reporting accuracy' },
      { value: 'Real-time', label: 'Financial visibility' },
    ],
    features: [
      { icon: 'ri-book-2-line', title: 'Medical Practice Bookkeeping', desc: 'Accurate, healthcare-specific bookkeeping with proper chart of accounts, expense categorization, and monthly reconciliation — always audit-ready.' },
      { icon: 'ri-pie-chart-2-line', title: 'Financial Reporting & Analytics', desc: 'Monthly P&L statements, balance sheets, cash flow reports, and custom KPI dashboards that give you a crystal-clear picture of your practice\'s financial health.' },
      { icon: 'ri-funds-line', title: 'Budgeting & Financial Forecasting', desc: 'Annual budget development, rolling forecasts, and scenario planning that help you make confident decisions about hiring, expansion, and capital investments.' },
      { icon: 'ri-shopping-cart-2-line', title: 'Group Purchasing Organization (GPO) Access', desc: 'Leverage our GPO network to reduce supply costs by 15–30% on medical supplies, pharmaceuticals, equipment, and office materials through collective buying power.' },
      { icon: 'ri-bank-line', title: 'Practice Financing & Banking Relationships', desc: 'Guidance on practice loans, equipment financing, and banking relationships — connecting you with healthcare-specialized lenders for the best terms.' },
      { icon: 'ri-file-chart-line', title: 'Tax Planning & CPA Coordination', desc: 'Year-round tax planning strategies for medical professionals, coordination with your CPA, and entity structure optimization to minimize your tax burden legally.' },
    ],
    who: ['Independent Practices', 'Group Practices', 'Pharmacies', 'Laboratories & Diagnostics'],
  },
];

const processSteps = [
  { step: '01', icon: 'ri-search-2-line', title: 'Discovery & Assessment', desc: 'We conduct a comprehensive operational audit of your practice — identifying gaps, inefficiencies, and opportunities across all service areas.' },
  { step: '02', icon: 'ri-map-2-line', title: 'Custom Roadmap', desc: 'Our team designs a tailored implementation plan with clear milestones, timelines, and measurable outcomes specific to your practice goals.' },
  { step: '03', icon: 'ri-rocket-line', title: 'Seamless Onboarding', desc: 'A dedicated onboarding specialist guides your team through every transition — minimizing disruption and ensuring staff confidence from day one.' },
  { step: '04', icon: 'ri-line-chart-line', title: 'Ongoing Optimization', desc: 'Monthly performance reviews, quarterly strategy sessions, and continuous improvement cycles keep your practice growing and adapting.' },
];

export default function ServicesPage() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeService, setActiveService] = useState(services[0].id);
  const [visibleSections, setVisibleSections] = useState<Set<string>>(new Set());
  const sectionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );
    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  const setRef = (id: string) => (el: HTMLDivElement | null) => {
    sectionRefs.current[id] = el;
  };

  const currentService = services.find((s) => s.id === activeService) || services[0];

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-gradient-to-b from-[#0A1628] to-[#1B4B7F]'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3 cursor-pointer">
              <img
                src="https://static.readdy.ai/image/50f4002bde5ecf02750e3107d0496ef3/6f24c25eed393f63407e0e21645fee7d.png"
                alt="MedxMSO Logo"
                className="h-12 w-auto"
              />
              <span className={`text-xl font-black tracking-tight whitespace-nowrap ${scrolled ? 'text-[#1B4B7F]' : 'text-white'}`}>MedxMSO</span>
            </Link>
            <div className="hidden lg:flex items-center space-x-8">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Home</Link>
              <Link to="/services" className={`text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#1B4B7F]' : 'text-white'}`}>Services</Link>
              <Link to="/pricing" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Models</Link>
              <Link to="/mso-pricing" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Vendor Pricing</Link>
              <Link to="/partnership" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#059669] hover:text-[#047857]' : 'text-[#6EE7B7] hover:text-white'}`}>Partnership</Link>
              <Link to="/why-mso" className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-gray-700 hover:text-[#1B4B7F]' : 'text-white hover:text-gray-200'}`}>Why MSO?</Link>
              <Link to="/audit" className={`text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-[#BE185D] hover:text-[#9D174D]' : 'text-[#F9A8D4] hover:text-white'}`}>Request Audit</Link>
              <Link to="/#contact" className="bg-[#1B4B7F] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-[#153a63] transition-colors whitespace-nowrap cursor-pointer">
                Get Started
              </Link>
            </div>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${scrolled ? 'text-gray-700' : 'text-white'}`}></i>
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t">
            <div className="px-6 py-4 space-y-3">
              <Link to="/" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Home</Link>
              <Link to="/services" className="block text-sm font-semibold text-[#1B4B7F] cursor-pointer">Services</Link>
              <Link to="/pricing" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Models</Link>
              <Link to="/mso-pricing" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Vendor Pricing</Link>
              <Link to="/partnership" className="block text-sm font-semibold text-[#059669] cursor-pointer">Partnership</Link>
              <Link to="/why-mso" className="block text-sm font-medium text-gray-700 hover:text-[#1B4B7F] cursor-pointer">Why MSO?</Link>
              <Link to="/audit" className="block text-sm font-semibold text-[#BE185D] cursor-pointer">Request Audit</Link>
              <Link to="/#contact" className="block bg-[#1B4B7F] text-white px-6 py-2.5 rounded-full text-sm font-medium text-center cursor-pointer">Get Started</Link>
            </div>
          </div>
        )}
      </nav>

      {/* HERO */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Professional%20healthcare%20management%20services%20team%20in%20modern%20medical%20office%20building%20with%20diverse%20group%20of%20administrators%20doctors%20and%20business%20professionals%20collaborating%20in%20bright%20spacious%20contemporary%20healthcare%20facility%20lobby%20with%20glass%20walls%20natural%20light%20and%20professional%20medical%20environment%20representing%20comprehensive%20MSO%20services&width=1920&height=900&seq=services-hero-bg&orientation=landscape"
            alt="MedxServices"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/80 to-[#0A1628]/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-40 w-full">
          <p className="text-xs uppercase tracking-[3px] text-[#0D9488] mb-6 font-semibold">COMPREHENSIVE MSO SOLUTIONS</p>
          <h1 className="text-6xl lg:text-8xl font-black text-white leading-none mb-6">
            Every Service<br />
            Your Practice<br />
            <span className="text-[#0D9488]">Will Ever Need.</span>
          </h1>
          <p className="text-xl text-gray-300 leading-relaxed mb-10 max-w-2xl">
            Seven deeply specialized service pillars — each one designed to eliminate a specific operational burden and replace it with measurable, expert-driven results.
          </p>
          <div className="flex flex-wrap gap-4">
            <a href="#service-explorer" className="bg-[#0D9488] text-white px-8 py-4 rounded-full text-sm font-medium hover:bg-[#0b7a70] transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer">
              Explore All Services
              <i className="ri-arrow-down-line"></i>
            </a>
            <Link to="/#contact" className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-full text-sm font-medium hover:bg-white hover:text-[#0A1628] transition-all whitespace-nowrap cursor-pointer">
              Request a Consultation
            </Link>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="bg-[#0A1628] py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { val: '500+', label: 'Practices Managed', sub: 'Across all entity types' },
              { val: '98%', label: 'Client Retention Rate', sub: 'Year over year' },
              { val: '$50M+', label: 'Revenue Recovered', sub: 'Through RCM services' },
              { val: '7', label: 'Core Service Pillars', sub: 'Fully integrated support' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-4xl font-black text-[#0D9488] mb-1">{s.val}</div>
                <div className="text-white font-semibold text-sm mb-1">{s.label}</div>
                <div className="text-gray-500 text-xs">{s.sub}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICE NAVIGATOR */}
      <section id="service-explorer" className="py-6 bg-[#FAF8F5] sticky top-20 z-40 border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
            {services.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  setActiveService(s.id);
                  document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all cursor-pointer flex-shrink-0 ${
                  activeService === s.id
                    ? 'bg-[#1B4B7F] text-white shadow-md'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                <i className={`${s.icon} text-base`}></i>
                {s.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* QUICK OVERVIEW CARDS */}
      <section className="py-20 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[3px] text-[#0D9488] mb-4 font-semibold">THE FULL PICTURE</p>
            <h2 className="text-5xl font-black text-gray-900 mb-4">Seven Pillars of Practice Excellence</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Each service is a standalone powerhouse — together, they form an unbeatable operational foundation.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s) => (
              <button
                key={s.id}
                onClick={() => {
                  setActiveService(s.id);
                  document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                className="bg-white rounded-2xl p-8 text-left hover:shadow-xl hover:-translate-y-1 transition-all duration-300 cursor-pointer border border-gray-100 group"
              >
                <div className={`w-12 h-12 bg-gradient-to-br ${s.color} rounded-xl flex items-center justify-center mb-5`}>
                  <i className={`${s.icon} text-white text-2xl`}></i>
                </div>
                <div className="text-xs font-bold tracking-widest mb-2" style={{ color: s.accent }}>{s.tag}</div>
                <h3 className="text-xl font-black text-gray-900 mb-3 group-hover:text-[#1B4B7F] transition-colors">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-4">{s.summary.slice(0, 100)}...</p>
                <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: s.accent }}>
                  Deep Dive
                  <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* DEEP DIVE SECTIONS */}
      {services.map((service, idx) => (
        <section
          key={service.id}
          id={service.id}
          className={`py-28 ${idx % 2 === 0 ? 'bg-white' : 'bg-[#FAF8F5]'}`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            {/* Section Header */}
            <div
              id={`header-${service.id}`}
              ref={setRef(`header-${service.id}`)}
              className={`transition-all duration-700 ${visibleSections.has(`header-${service.id}`) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
                <div>
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold tracking-widest mb-6 text-white bg-gradient-to-r ${service.color}`}>
                    <i className={`${service.icon} text-base`}></i>
                    {service.tag}
                  </div>
                  <h2 className="text-5xl lg:text-6xl font-black text-gray-900 leading-tight mb-4">{service.title}</h2>
                  <p className="text-xl font-semibold mb-6" style={{ color: service.accent }}>{service.tagline}</p>
                  <p className="text-lg text-gray-600 leading-relaxed mb-8">{service.summary}</p>
                  {/* Stats Row */}
                  <div className="flex flex-wrap gap-6">
                    {service.stats.map((stat, i) => (
                      <div key={i} className="text-center">
                        <div className="text-3xl font-black mb-1" style={{ color: service.accent }}>{stat.value}</div>
                        <div className="text-xs text-gray-500 font-medium">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl h-80 lg:h-96">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t from-black/40 to-transparent`}></div>
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="flex flex-wrap gap-2">
                      {service.who.map((w, i) => (
                        <span key={i} className="bg-white/90 text-gray-800 text-xs font-semibold px-3 py-1.5 rounded-full">{w}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Features Grid */}
            <div
              id={`features-${service.id}`}
              ref={setRef(`features-${service.id}`)}
              className={`transition-all duration-700 delay-200 ${visibleSections.has(`features-${service.id}`) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <div className="mb-10">
                <h3 className="text-2xl font-black text-gray-900 mb-2">What's Included</h3>
                <div className="w-16 h-1 rounded-full" style={{ backgroundColor: service.accent }}></div>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {service.features.map((feature, i) => (
                  <div
                    key={i}
                    className="bg-white rounded-2xl p-7 border border-gray-100 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
                    style={{ borderTop: `3px solid ${service.accent}` }}
                  >
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${service.accent}15` }}>
                      <i className={`${feature.icon} text-xl`} style={{ color: service.accent }}></i>
                    </div>
                    <h4 className="text-base font-black text-gray-900 mb-3 leading-tight">{feature.title}</h4>
                    <p className="text-gray-500 text-sm leading-relaxed">{feature.desc}</p>
                  </div>
                ))}
              </div>

              {/* Audit Tiers — only for audit-review */}
              {'auditTiers' in service && (service as typeof service & { auditTiers: { name: string; badge: string; badgeColor: string; desc: string; includes: string[]; cta: string }[] }).auditTiers && (
                <div className="mt-14 mb-2">
                  <div className="mb-10">
                    <h3 className="text-2xl font-black text-gray-900 mb-2">Choose Your Audit Level</h3>
                    <div className="w-16 h-1 rounded-full bg-[#BE185D]"></div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-8">
                    {(service as typeof service & { auditTiers: { name: string; badge: string; badgeColor: string; desc: string; includes: string[]; cta: string }[] }).auditTiers.map((tier, ti) => (
                      <div
                        key={ti}
                        className={`rounded-3xl border-2 p-10 relative overflow-hidden ${ti === 1 ? 'border-[#BE185D] bg-[#FFF1F5]' : 'border-gray-200 bg-white'}`}
                      >
                        {ti === 1 && (
                          <div className="absolute top-0 right-0 bg-[#BE185D] text-white text-xs font-black px-5 py-2 rounded-bl-2xl tracking-widest">
                            MOST POPULAR
                          </div>
                        )}
                        <div
                          className="inline-block text-xs font-black tracking-widest px-3 py-1 rounded-full mb-5 text-white"
                          style={{ backgroundColor: tier.badgeColor }}
                        >
                          {tier.badge}
                        </div>
                        <h4 className="text-2xl font-black text-gray-900 mb-3">{tier.name}</h4>
                        <p className="text-gray-500 text-sm leading-relaxed mb-7">{tier.desc}</p>
                        <ul className="space-y-3 mb-8">
                          {tier.includes.map((item, ii) => (
                            <li key={ii} className="flex items-start gap-3 text-sm text-gray-700">
                              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <i className="ri-check-line font-bold" style={{ color: tier.badgeColor }}></i>
                              </div>
                              {item}
                            </li>
                          ))}
                        </ul>
                        <Link
                          to="/#contact"
                          className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-sm font-bold transition-all whitespace-nowrap cursor-pointer text-white"
                          style={{ backgroundColor: tier.badgeColor }}
                        >
                          {tier.cta}
                          <i className="ri-arrow-right-line"></i>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA for this service */}
              <div className={`mt-12 bg-gradient-to-r ${service.color} rounded-3xl p-10 flex flex-col lg:flex-row items-center justify-between gap-6`}>
                <div>
                  <h4 className="text-2xl font-black text-white mb-2">Ready to get started with {service.title}?</h4>
                  <p className="text-white/80 text-sm">Schedule a free consultation and see how we can transform this area of your practice.</p>
                </div>
                <div className="flex gap-4 flex-shrink-0">
                  <Link to="/#contact" className="bg-white text-gray-900 px-8 py-3.5 rounded-full text-sm font-bold hover:bg-gray-100 transition-all whitespace-nowrap cursor-pointer">
                    Get a Free Assessment
                  </Link>
                  <Link to="/pricing" className="bg-white/20 text-white border border-white/40 px-8 py-3.5 rounded-full text-sm font-bold hover:bg-white/30 transition-all whitespace-nowrap cursor-pointer">
                    View Pricing
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* HOW IT WORKS */}
      <section className="py-28 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <p className="text-xs uppercase tracking-[3px] text-[#0D9488] mb-4 font-semibold">THE MEDXMSO PROCESS</p>
            <h2 className="text-5xl lg:text-6xl font-black text-white leading-tight mb-6">
              From Day One to<br />
              <span className="text-[#0D9488]">Long-Term Growth</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Our proven 4-step onboarding and optimization process ensures a seamless transition and measurable results from the start.
            </p>
          </div>
          <div
            id="process-steps"
            ref={setRef('process-steps')}
            className={`grid md:grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-700 ${visibleSections.has('process-steps') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
          >
            {processSteps.map((step, i) => (
              <div key={i} className="relative">
                {i < processSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-[#0D9488]/50 to-transparent z-0 -translate-y-1/2"></div>
                )}
                <div className="bg-white/5 border border-white/10 rounded-3xl p-8 relative z-10 hover:bg-white/10 transition-all duration-300">
                  <div className="text-6xl font-black text-white/10 leading-none mb-4">{step.step}</div>
                  <div className="w-12 h-12 bg-[#0D9488]/20 rounded-2xl flex items-center justify-center mb-5">
                    <i className={`${step.icon} text-[#0D9488] text-2xl`}></i>
                  </div>
                  <h3 className="text-lg font-black text-white mb-3">{step.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ENTITY TYPES */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[3px] text-[#0D9488] mb-4 font-semibold">WHO WE SERVE</p>
            <h2 className="text-5xl font-black text-gray-900 mb-4">Tailored for Every Healthcare Entity</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">Our services are customized to the unique operational needs of each healthcare entity type.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: 'ri-hospital-line', title: 'Private Practices', desc: 'Solo and small group practices looking to professionalize operations and grow sustainably.', services: ['Practice Management', 'RCM', 'HR', 'Compliance'] },
              { icon: 'ri-building-4-line', title: 'Group Clinics', desc: 'Multi-provider clinics needing centralized management, unified billing, and coordinated HR.', services: ['All 7 Services', 'Multi-site Coordination', 'GPO Access'] },
              { icon: 'ri-heart-pulse-line', title: 'Specialty Centers', desc: 'Cardiology, orthopedics, dermatology, and other specialty practices with unique coding needs.', services: ['Specialty Coding', 'Credentialing', 'Technology'] },
              { icon: 'ri-medicine-bottle-line', title: 'Pharmacies', desc: 'Retail and compounding pharmacies requiring compliance, inventory, and financial management.', services: ['Compliance', 'Financial Ops', 'HR', 'Technology'] },
              { icon: 'ri-microscope-line', title: 'Laboratories', desc: 'Clinical and reference labs needing billing optimization, CLIA compliance, and operational support.', services: ['RCM', 'Compliance', 'Technology', 'Finance'] },
              { icon: 'ri-scan-line', title: 'Diagnostic Centers', desc: 'Imaging, radiology, and pathology centers with complex billing and accreditation requirements.', services: ['RCM', 'Credentialing', 'IT Integration', 'Finance'] },
            ].map((entity, i) => (
              <div key={i} className="bg-[#FAF8F5] rounded-2xl p-8 hover:shadow-lg transition-all duration-300 border border-gray-100">
                <div className="w-12 h-12 bg-gradient-to-br from-[#1B4B7F] to-[#0D9488] rounded-xl flex items-center justify-center mb-5">
                  <i className={`${entity.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-3">{entity.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed mb-5">{entity.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {entity.services.map((svc, j) => (
                    <span key={j} className="bg-white border border-gray-200 text-gray-600 text-xs font-semibold px-3 py-1 rounded-full">{svc}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Successful%20medical%20practice%20team%20celebrating%20achievement%20in%20modern%20bright%20healthcare%20office%20with%20diverse%20doctors%20nurses%20and%20administrators%20smiling%20and%20collaborating%20representing%20thriving%20medical%20practice%20with%20professional%20management%20support%20and%20operational%20excellence&width=1920&height=900&seq=services-cta-final&orientation=landscape"
            alt="Thriving Practice"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/95 via-[#0A1628]/80 to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[3px] text-[#0D9488] mb-6 font-semibold">YOUR NEXT STEP</p>
            <h2 className="text-6xl lg:text-7xl font-black text-white leading-none mb-8">
              Let's Build<br />
              Your Ideal<br />
              <span className="text-[#0D9488]">Practice.</span>
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-10">
              Start with one service or all seven. Our team will design a custom support package that fits your practice size, specialty, and growth goals — with transparent pricing and no long-term lock-in.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/#contact" className="bg-[#0D9488] text-white px-10 py-5 rounded-full text-base font-bold hover:bg-[#0b7a70] transition-all flex items-center gap-3 whitespace-nowrap cursor-pointer shadow-2xl">
                Schedule Free Consultation
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
              <Link to="/pricing" className="bg-transparent border-2 border-white text-white px-10 py-5 rounded-full text-base font-bold hover:bg-white hover:text-[#0A1628] transition-all whitespace-nowrap cursor-pointer">
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1628] relative overflow-hidden">
        <div className="absolute bottom-0 left-0 right-0 text-[#1A2942] text-[180px] font-bold opacity-50 leading-none select-none pointer-events-none">
          MEDXMSO
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid md:grid-cols-4 gap-12">
            <div>
              <div className="text-white text-4xl font-bold mb-3">MedxMSO</div>
              <p className="text-gray-400 text-sm mb-16">Healthcare Management Excellence</p>
              <div className="text-white text-lg leading-relaxed">
                <p>Empowering providers</p>
                <p className="text-gray-400">since 2015</p>
              </div>
            </div>
            <div>
              <h4 className="text-white text-lg font-bold mb-6">Our Services</h4>
              <ul className="space-y-4">
                {services.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' })}
                      className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer text-left"
                    >
                      {s.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-white text-lg font-bold mb-6">Pages</h4>
              <ul className="space-y-4">
                <li><Link to="/" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/services" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Services</Link></li>
                <li><Link to="/pricing" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Pricing</Link></li>
                <li><Link to="/partnership" className="text-[#6EE7B7] text-sm hover:text-white transition-colors cursor-pointer">Partnership</Link></li>
                <li><Link to="/why-mso" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Why MSO?</Link></li>
                <li><Link to="/#contact" className="text-gray-400 text-sm hover:text-white transition-colors cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="text-white text-lg font-bold mb-6">Connect</h4>
              <ul className="space-y-4">
                <li><a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer"><i className="ri-linkedin-line text-xl"></i>LinkedIn</a></li>
                <li><a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer"><i className="ri-twitter-x-line text-xl"></i>Twitter</a></li>
                <li><a href="#" className="flex items-center gap-3 text-gray-400 text-sm hover:text-white transition-colors cursor-pointer"><i className="ri-facebook-line text-xl"></i>Facebook</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-16 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">© 2024 MedxMSO. All rights reserved.</p>
            <div className="flex gap-6">
              <a href="#" className="text-gray-500 text-sm hover:text-white transition-colors cursor-pointer">Privacy Policy</a>
              <a href="#" className="text-gray-500 text-sm hover:text-white transition-colors cursor-pointer">Terms of Service</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
